import * as React from 'react';
import { renderNumber, parseNumToPercentage } from '@src/shared/utils';
import { BaseChart } from '../BaseChart';
import { CHART_COLORS } from '../BaseChart/options';
import Tooltip from './Tooltip';
import { createDiagonalPattern, computeLabelWidth } from './utils';
import type { EChartsOption } from 'echarts';

export interface FunnelChartProps {
  pattern: { id: string; name: string }[];
  data?: PatternQueryResponseItem<number>[];
  aggregation?: Aggregation;
  tooltipAggregations?: Aggregation[];
  className?: string;
  style?: React.CSSProperties;
  onDropoffClick?: (index: number) => void;
}

const FunnelChart = (props: FunnelChartProps) => {
  const { pattern, data = [], aggregation, tooltipAggregations = [], onDropoffClick, ...p } = props;
  const tooltipAggregationMap = React.useMemo(() => {
    return tooltipAggregations.reduce((acc, item) => {
      acc.set(item.id, item);
      return acc;
    }, new Map<string, Aggregation>());
  }, [tooltipAggregations]);
  const dataRef = React.useRef(data);
  const onDropoffClickRef = React.useRef(onDropoffClick);

  dataRef.current = data;
  onDropoffClickRef.current = onDropoffClick;

  const chartOptions = React.useMemo<GetProp<typeof BaseChart, 'options'>>(
    () => (instance) => {
      const isPct = aggregation?.unit === '%';
      const labels = pattern.map((item, index) => `Step ${index + 1}: ${item.name}`);
      const seriesData = aggregation ? data.map((item) => item[aggregation.id]) : [];
      const dropoffData = pattern
        .slice(0, data.length + 1)
        .map((item, index) =>
          index === 0 ? null : seriesData[index - 1] - (seriesData[index] ?? 0),
        );

      const options: EChartsOption = {
        tooltip: {
          trigger: 'item',
        },
        xAxis: {
          type: 'category',
          data: labels,
          axisLabel: {
            width: computeLabelWidth(instance!, labels.length),
          },
        },
        yAxis: {
          type: 'value',
          min: 0,
          max: isPct ? 1 : undefined,
          axisLabel: {
            formatter: (value: number) => (isPct ? `${value * 100}%` : value.toString()),
          },
        },
        series: [
          {
            name: 'conversion',
            type: 'bar',
            stack: 'all',
            data: seriesData,
            itemStyle: {
              color: (p) => CHART_COLORS[p.dataIndex % CHART_COLORS.length],
            },
            zlevel: 2,
            label: {
              show: true,
              position: 'insideTop',
              fontSize: 12,
              color: '#00000073',
              formatter: (p) => {
                if (!p) return '';
                const { value } = p;

                if (isPct && typeof value === 'number') {
                  return parseNumToPercentage(value);
                }

                if (typeof value === 'number') return renderNumber(value);

                if (typeof value === 'string') return value;

                return '';
              },
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.3)',
              },
            },
          },
          {
            name: 'dropoff',
            type: 'bar',
            stack: 'all',
            data: dropoffData,
            itemStyle: {
              color: (p) => {
                const baseColor = CHART_COLORS[p.dataIndex % CHART_COLORS.length];
                return createDiagonalPattern(baseColor);
              },
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 8,
                shadowOffsetX: 0,
                shadowColor: 'rgba(255, 0, 0, 0.3)',
              },
            },
          },
        ],
      };

      return options;
    },
    [aggregation, pattern, data],
  );

  const handleDropoffClick = React.useCallback(
    (params: any) => {
      if (
        !params ||
        params.seriesName !== 'dropoff' ||
        typeof params.dataIndex !== 'number' ||
        !pattern[params.dataIndex]
      ) {
        return;
      }

      onDropoffClick?.(params.dataIndex);
    },
    [onDropoffClick, pattern],
  );

  const events = React.useMemo(
    () => ({
      click: {
        handler: handleDropoffClick,
      },
    }),
    [handleDropoffClick],
  );

  const tooltipRenderer = React.useCallback<GetProp<typeof BaseChart, 'customTooltip'>>(
    ([p]) => {
      const { dataIndex, seriesName } = p;
      const rawRecord = dataRef.current[dataIndex];

      if (!rawRecord && seriesName !== 'dropoff') return null;

      const tooltipData = Array.from(
        tooltipAggregationMap.entries().map(([id, metadata]) => {
          const isPct = metadata?.unit === '%';
          const rawData =
            seriesName === 'dropoff' && dataIndex > 0
              ? dataRef.current[dataIndex - 1][id] - (rawRecord?.[id] ?? 0)
              : rawRecord[id];

          return {
            label: metadata.name,
            value: isPct ? parseNumToPercentage(rawData) : renderNumber(rawData),
          };
        }),
      );

      return (
        <Tooltip
          type={seriesName! as 'conversion' | 'dropoff'}
          data={tooltipData}
          onViewGapsClick={() => {
            onDropoffClickRef.current?.(dataIndex);
          }}
        />
      );
    },
    [tooltipAggregationMap],
  );

  return (
    <BaseChart
      {...p}
      defaultRenderer="canvas"
      options={chartOptions}
      events={data.length ? events : undefined}
      customTooltip={data.length ? tooltipRenderer : undefined}
    />
  );
};

export default FunnelChart;
